import type { GameItem, SubmissionView } from "@/types/api";
import { Camera, Clock, CheckCircle2, XCircle, RotateCcw } from "lucide-react";

interface ItemChecklistProps {
  items: GameItem[];
  submissions: SubmissionView[];
  myPlayerId: string;
  canSubmit: boolean;
  onCapture: (itemId: string) => void;
}

function getItemStatus(
  itemId: string,
  submissions: SubmissionView[],
  myPlayerId: string,
): SubmissionView | undefined {
  return submissions.find(
    (s) => s.itemId === itemId && s.playerId === myPlayerId,
  );
}

export default function ItemChecklist({
  items,
  submissions,
  myPlayerId,
  canSubmit,
  onCapture,
}: ItemChecklistProps) {
  return (
    <ul className="space-y-3">
      {items.map((item, i) => {
        const sub = getItemStatus(item.itemId, submissions, myPlayerId);
        const status = sub?.moderationStatus;
        const canTap = canSubmit && (!status || status === "rejected");
        const hasImage = sub?.imageUrl;

        return (
          <li key={item.itemId} className="animate-fade-in-up" style={{ animationDelay: `${i * 50}ms` }}>
            <button
              disabled={!canTap}
              onClick={() => canTap && onCapture(item.itemId)}
              className={`flex w-full gap-3 rounded-2xl border-2 p-3 text-left transition-all ${
                status === "approved"
                  ? "border-accent/30 bg-accent/5"
                  : status === "pending"
                    ? "border-warning/30 bg-warning/5"
                    : status === "rejected"
                      ? "border-error/30 bg-error/5"
                      : canTap
                        ? "border-border hover:border-primary hover:bg-primary/5 cursor-pointer hover:-translate-y-0.5 hover:shadow-md"
                        : "border-border/50 cursor-default"
              }`}
            >
              {/* Thumbnail or status icon */}
              <div className={`relative flex h-16 w-16 shrink-0 items-center justify-center overflow-hidden rounded-xl ${
                hasImage ? "" :
                !status ? "bg-primary/10" :
                status === "pending" ? "bg-warning/10" :
                status === "approved" ? "bg-accent/10" :
                "bg-error/10"
              }`}>
                {hasImage ? (
                  <>
                    <img
                      src={sub.imageUrl}
                      alt={item.name}
                      className="h-full w-full object-cover"
                      loading="lazy"
                    />
                    {/* Status overlay on thumbnail */}
                    <div className={`absolute inset-0 flex items-center justify-center ${
                      status === "approved" ? "bg-accent/30" :
                      status === "pending" ? "bg-warning/30" :
                      status === "rejected" ? "bg-error/40" : ""
                    }`}>
                      {status === "approved" && <CheckCircle2 className="h-7 w-7 text-white drop-shadow-md" />}
                      {status === "pending" && <Clock className="h-6 w-6 text-white drop-shadow-md" />}
                      {status === "rejected" && <XCircle className="h-7 w-7 text-white drop-shadow-md" />}
                    </div>
                  </>
                ) : (
                  <>
                    {!status && <Camera className="h-7 w-7 text-primary" />}
                    {status === "pending" && <Clock className="h-7 w-7 text-warning" />}
                    {status === "approved" && <CheckCircle2 className="h-7 w-7 text-accent" />}
                    {status === "rejected" && <XCircle className="h-7 w-7 text-error" />}
                  </>
                )}
              </div>

              {/* Content */}
              <div className="min-w-0 flex-1 py-1">
                <span className={`text-base font-bold ${
                  status === "approved" ? "text-accent" : "text-text"
                }`}>
                  {item.name}
                </span>
                {item.description && (
                  <p className="mt-0.5 text-sm text-text-secondary line-clamp-1">
                    {item.description}
                  </p>
                )}
                {/* Status label */}
                <div className="mt-1.5">
                  {!status && canTap && (
                    <span className="text-xs font-semibold text-primary uppercase tracking-wide">
                      Tap to photograph
                    </span>
                  )}
                  {status === "pending" && (
                    <span className="text-xs font-semibold text-warning uppercase tracking-wide">
                      Waiting for review
                    </span>
                  )}
                  {status === "approved" && (
                    <span className="text-xs font-semibold text-accent uppercase tracking-wide">
                      Found!
                    </span>
                  )}
                  {status === "rejected" && (
                    <span className="inline-flex items-center gap-1 text-xs font-semibold text-error uppercase tracking-wide">
                      <RotateCcw className="h-3 w-3" />
                      Try again
                    </span>
                  )}
                </div>
              </div>
            </button>
          </li>
        );
      })}
    </ul>
  );
}
